import DOMPurify from 'dompurify';
import {marked} from 'marked';
import type {ChatMessage, ChatSnapshot, ChatStateChange, ChatToolCall, ChatToolCallStatus} from './chat-state';

declare const howspace: any;

type Labels = {
    placeholder: string;
    send: string;
    emptyState: string;
    thinking: string;
    thoughtFor: string;
    fewSeconds: string;
    seconds: string;
    copy: string;
    close: string;
    toolCalls: string;
    toolStatusRunning: string;
    toolStatusDone: string;
    toolStatusError: string;
    toolArguments: string;
    toolResult: string;
    modelChangedWarning: string;
    threadStaleWarning: string;
    runTimeoutWarning: string;
    queueDelayNotice: string;
    error: string;
};

type TransientRunStatus = 'thinking' | 'tool';

type ChatRenderItem = (
    | {key: string; kind: 'welcome'; message: string}
    | {key: string; kind: 'message'; message: ChatMessage; isStreaming: boolean}
    | {key: string; kind: 'reasoning'; message: ChatMessage; isActive: boolean}
    | {key: string; kind: 'thinking-placeholder'; text: string; status: TransientRunStatus}
);

type RenderedItem = {
    kind: ChatRenderItem['kind'];
    signature: string;
    fingerprint: string;
    element: HTMLElement;
    content: string;
    // Streaming state is canonical immediately, but active assistant/reasoning
    // text renders through this visible buffer so large deltas still read as typed text.
    displayedContent: string;
    smoothingFrame: number | null;
    // When a run finishes before the buffer catches up, keep the cursor/open reasoning
    // state until the last visible frame has rendered.
    removeStreamingClassWhenSmooth: boolean;
    toolCallsFingerprint: string;
    message?: ChatMessage;
};

const STREAM_INITIAL_VISIBLE_CHARS = 8;
const STREAM_SMOOTHING_MAX_CHARS = 12;
const STICKY_SCROLL_THRESHOLD_PX = 48;
const QUEUED_RUN_NOTICE_DELAY_MS = 5000;
const THINKING_PLACEHOLDER_ERASE_CHARS = 2;
const THINKING_PLACEHOLDER_TYPE_CHARS = 3;

export type ChatUiOptions = {
    labels: Labels;
    showReasoning: boolean;
    showToolCalls: boolean;
    showFriendlyToolStatus: boolean;
    toolLabels: Record<string, string>;
    modelMismatch: boolean;
    openingQuestion?: string;
    onSubmit: (message: string) => void;
};

export class ChatUi {
    private readonly root: HTMLElement;
    private readonly messages: HTMLElement;
    private readonly form: HTMLFormElement;
    private readonly textarea: HTMLTextAreaElement;
    private readonly submitButton: HTMLButtonElement;
    private readonly status: HTMLElement;
    private readonly renderedItems = new Map<string, RenderedItem>();
    private readonly copyButtonContent = new WeakMap<HTMLButtonElement, string>();
    private renderedOrder: string[] = [];
    private statusSignature = '';
    private activeAssistantKey: string | null = null;
    private activeReasoningKey: string | null = null;
    private queuedRunNoticeTimer: number | null = null;
    private thinkingPlaceholderFrame: number | null = null;
    private running = false;

    constructor(container: HTMLElement, private readonly options: ChatUiOptions) {
        this.root = document.createElement('div');
        this.root.className = 'howspace-ai-chat';

        if (options.modelMismatch) {
            const warning = document.createElement('div');
            warning.className = 'howspace-ai-chat-warning';

            const warningText = document.createElement('span');
            warningText.className = 'howspace-ai-chat-warning-text';
            warningText.textContent = options.labels.modelChangedWarning;

            const closeLabel = options.labels.close || 'Close';
            const closeButton = document.createElement('button');
            closeButton.type = 'button';
            closeButton.className = 'howspace-ai-chat-warning-close';
            closeButton.setAttribute('aria-label', closeLabel);
            closeButton.title = closeLabel;
            closeButton.innerHTML = '<i class="fa-light fa-xmark" aria-hidden="true"></i>';
            closeButton.addEventListener('click', () => {
                warning.remove();
            });

            warning.append(warningText, closeButton);
            this.root.append(warning);
        }

        this.messages = document.createElement('div');
        this.messages.className = 'howspace-ai-chat-messages';
        this.messages.setAttribute('aria-live', 'polite');
        this.root.append(this.messages);

        this.status = document.createElement('div');
        this.status.className = 'howspace-ai-chat-status';
        this.status.setAttribute('aria-live', 'polite');
        this.root.append(this.status);

        this.form = document.createElement('form');
        this.form.className = 'howspace-ai-chat-composer';

        const inputShell = document.createElement('div');
        inputShell.className = 'howspace-ai-chat-composer-input';

        this.textarea = document.createElement('textarea');
        this.textarea.className = 'howspace-ai-chat-input';
        this.textarea.rows = 1;
        this.textarea.placeholder = options.labels.placeholder;
        this.textarea.setAttribute('aria-label', options.labels.placeholder);

        this.submitButton = document.createElement('button');
        this.submitButton.type = 'submit';
        this.submitButton.className = 'howspace-ai-chat-send';
        this.submitButton.setAttribute('aria-label', options.labels.send);
        this.submitButton.title = options.labels.send;
        this.submitButton.innerHTML = '<i class="fa-light fa-arrow-down howspace-ai-chat-send-icon" aria-hidden="true"></i>';

        inputShell.append(this.textarea, this.submitButton);
        this.form.append(inputShell);
        this.root.append(this.form);
        container.prepend(this.root);

        this.form.addEventListener('submit', event => {
            event.preventDefault();
            const message = this.textarea.value.trim();
            if (!message || this.running || this.submitButton.disabled) {
                return;
            }
            this.options.onSubmit(message);
            this.textarea.value = '';
            this.resizeTextarea();
            this.updateSubmitState();
        });

        this.textarea.addEventListener('input', () => {
            this.resizeTextarea();
            this.updateSubmitState();
        });

        this.textarea.addEventListener('keydown', event => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.form.requestSubmit();
            }
        });

        this.resizeTextarea();
        this.updateSubmitState();
        this.focusComposer();
    }

    // Bootstrap/replay can build the whole transcript. Streaming updates must
    // go through applyChange so completed history stays inert.
    renderInitial(snapshot: ChatSnapshot): void {
        this.setRunning(snapshot.running);
        this.renderStatus(snapshot);
        this.updateSubmitState();

        const items = this.buildRenderItems(snapshot);
        if (this.patchRenderedItems(items)) {
            this.scrollToBottom();
        }
        this.syncActiveItems(items);
    }

    applyChange(change: ChatStateChange | null): void {
        if (!change) {
            return;
        }

        const stickToBottom = this.isNearBottom();
        let changed = false;
        this.setRunning(change.running);
        this.renderStatus(change);

        switch (change.event.type) {
            case 'RUN_STARTED':
                this.activeAssistantKey = null;
                this.activeReasoningKey = null;
                change.messages.forEach(message => {
                    if (this.upsertChangedMessage(message)) {
                        changed = true;
                    }
                });
                changed = this.showThinkingStatus() || changed;
                break;

            case 'RUN_FINISHED':
            case 'RUN_ERROR':
                if (this.finishActiveRunItems()) {
                    changed = true;
                }
                change.messages.forEach(message => {
                    if (this.upsertChangedMessage(message)) {
                        changed = true;
                    }
                });
                break;

            case 'TEXT_MESSAGE_START':
                changed = this.syncTextMessageStartStatus() || changed;
                break;

            case 'TEXT_MESSAGE_CONTENT':
                changed = this.clearTransientRunStatus() || changed;
                change.messages.forEach(message => {
                    if (message.role === 'assistant' && message.content.trim() !== '') {
                        if (this.activateAssistantMessage(message)) {
                            changed = true;
                        }
                        return;
                    }

                    if (this.upsertChangedMessage(message)) {
                        changed = true;
                    }
                });
                break;

            case 'REASONING_START':
            case 'REASONING_MESSAGE_START':
            case 'REASONING_MESSAGE_CONTENT':
            case 'REASONING_MESSAGE_END':
            case 'REASONING_END':
                changed = this.syncReasoningStatus() || changed;
                change.messages.forEach(message => {
                    if (message.role === 'reasoning') {
                        if (this.activateReasoningMessage(message)) {
                            changed = true;
                        }
                        return;
                    }

                    if (this.upsertChangedMessage(message)) {
                        changed = true;
                    }
                });
                break;

            case 'TOOL_CALL_START':
            case 'TOOL_CALL_ARGS':
            case 'TOOL_CALL_END':
            case 'TOOL_CALL_RESULT':
                changed = this.syncToolStatus(change.messages) || changed;
                change.messages.forEach(message => {
                    if (this.upsertChangedMessage(message)) {
                        changed = true;
                    }
                });
                break;

            default:
                change.messages.forEach(message => {
                    if (this.upsertChangedMessage(message)) {
                        changed = true;
                    }
                });
                break;
        }

        this.updateSubmitState();
        if (change.event.type === 'RUN_FINISHED' || change.event.type === 'RUN_ERROR') {
            this.focusComposer();
        }
        if (changed && stickToBottom) {
            this.scrollToBottom();
        }
    }

    destroy(): void {
        this.clearQueuedRunNotice();
        this.renderedItems.forEach(rendered => this.clearTextSmoothing(rendered));
        this.renderedItems.clear();
        this.renderedOrder = [];
        this.activeAssistantKey = null;
        this.activeReasoningKey = null;
        this.root.remove();
    }

    setDraft(message: string): void {
        this.textarea.value = message;
        this.resizeTextarea();
        this.updateSubmitState();
    }

    startQueuedRunNoticeTimer(): void {
        this.clearQueuedRunNotice();
        if (!this.options.labels.queueDelayNotice) {
            return;
        }

        this.queuedRunNoticeTimer = window.setTimeout(() => {
            this.queuedRunNoticeTimer = null;
            if (!this.running) {
                return;
            }
            this.transitionThinkingPlaceholder(this.options.labels.queueDelayNotice);
        }, QUEUED_RUN_NOTICE_DELAY_MS);
    }

    clearQueuedRunNotice(): void {
        this.clearQueuedRunNoticeTimer();
        if (this.thinkingPlaceholderFrame !== null) {
            window.cancelAnimationFrame(this.thinkingPlaceholderFrame);
            this.thinkingPlaceholderFrame = null;
        }
        this.resetThinkingPlaceholder();
    }

    private clearQueuedRunNoticeTimer(): void {
        if (this.queuedRunNoticeTimer !== null) {
            window.clearTimeout(this.queuedRunNoticeTimer);
            this.queuedRunNoticeTimer = null;
        }
    }

    private setRunning(running: boolean): void {
        this.running = running;
        if (!running) {
            this.clearQueuedRunNotice();
        }
        const value = String(running);
        if (this.root.dataset.running !== value) {
            this.root.dataset.running = value;
        }
    }

    private focusComposer(): void {
        window.requestAnimationFrame(() => {
            if (this.root.isConnected) {
                this.textarea.focus({preventScroll: true});
            }
        });
    }

    private syncActiveItems(items: ChatRenderItem[]): void {
        this.activeAssistantKey = null;
        this.activeReasoningKey = null;
        items.forEach(item => {
            if (item.kind === 'message' && item.isStreaming) {
                this.activeAssistantKey = item.key;
            }
            if (item.kind === 'reasoning' && item.isActive) {
                this.activeReasoningKey = item.key;
            }
        });
    }

    private upsertChangedMessage(message: ChatMessage): boolean {
        if (message.role === 'reasoning') {
            return this.upsertReasoningMessage(message, this.activeReasoningKey === reasoningKey(message));
        }

        if (message.role === 'assistant' && message.content.trim() !== '' && this.running) {
            return this.activateAssistantMessage(message);
        }

        return this.upsertVisibleMessage(message, this.activeAssistantKey === messageKey(message) && this.running);
    }

    private activateAssistantMessage(message: ChatMessage): boolean {
        const key = messageKey(message);
        let changed = false;

        if (this.clearTransientRunStatus()) {
            changed = true;
        }
        if (this.deactivateActiveReasoning()) {
            changed = true;
        }
        if (this.activeAssistantKey && this.activeAssistantKey !== key && this.deactivateActiveAssistant()) {
            changed = true;
        }

        this.activeAssistantKey = this.running ? key : null;
        if (this.upsertVisibleMessage(message, this.running)) {
            changed = true;
        }

        return changed;
    }

    private activateReasoningMessage(message: ChatMessage): boolean {
        const key = reasoningKey(message);
        let changed = false;

        if (this.activeReasoningKey && this.activeReasoningKey !== key && this.deactivateActiveReasoning()) {
            changed = true;
        }

        const isActive = this.running && this.activeAssistantKey === null;
        this.activeReasoningKey = isActive ? key : null;
        if (this.upsertReasoningMessage(message, isActive)) {
            changed = true;
        }

        return changed;
    }

    private finishActiveRunItems(): boolean {
        let changed = false;
        if (this.clearTransientRunStatus()) {
            changed = true;
        }
        if (this.deactivateActiveReasoning()) {
            changed = true;
        }
        if (this.deactivateActiveAssistant()) {
            changed = true;
        }
        return changed;
    }

    private deactivateActiveReasoning(): boolean {
        const key = this.activeReasoningKey;
        this.activeReasoningKey = null;
        if (!key) {
            return false;
        }

        const rendered = this.renderedItems.get(key);
        if (!rendered?.message) {
            return false;
        }

        return this.upsertReasoningMessage(rendered.message, false);
    }

    private deactivateActiveAssistant(): boolean {
        const key = this.activeAssistantKey;
        this.activeAssistantKey = null;
        if (!key) {
            return false;
        }

        const rendered = this.renderedItems.get(key);
        if (!rendered?.message) {
            return false;
        }

        return this.upsertVisibleMessage(rendered.message, false);
    }

    private syncTextMessageStartStatus(): boolean {
        if (!this.options.showFriendlyToolStatus) {
            return false;
        }

        return this.clearTransientRunStatus();
    }

    private syncReasoningStatus(): boolean {
        if (!this.options.showFriendlyToolStatus) {
            return this.clearTransientRunStatus();
        }

        if (this.activeAssistantKey !== null) {
            return false;
        }

        return this.showThinkingStatus();
    }

    private syncToolStatus(messages: ChatMessage[]): boolean {
        if (!this.options.showFriendlyToolStatus) {
            return this.clearTransientRunStatus();
        }

        return this.showToolStatus(messages);
    }

    private showThinkingStatus(): boolean {
        return this.ensureTransientRunStatus(this.options.labels.thinking, 'thinking');
    }

    private showToolStatus(messages: readonly ChatMessage[]): boolean {
        const label = this.friendlyToolStatusLabel(messages);
        if (label === '') {
            return this.showThinkingStatus();
        }

        return this.ensureTransientRunStatus(label, 'tool');
    }

    private ensureTransientRunStatus(text: string, status: TransientRunStatus): boolean {
        const rendered = this.renderedItems.get('thinking-placeholder');
        if (rendered) {
            let changed = false;
            if (text !== this.thinkingPlaceholderTextElement()?.textContent) {
                this.transitionThinkingPlaceholder(text);
                changed = true;
            }
            if (this.setThinkingPlaceholderStatus(status)) {
                changed = true;
            }
            rendered.content = text;
            rendered.displayedContent = text;
            return changed;
        }

        this.removeWelcome();
        return this.upsertRenderedItem({
            key: 'thinking-placeholder',
            kind: 'thinking-placeholder',
            text,
            status,
        });
    }

    private clearTransientRunStatus(): boolean {
        this.clearQueuedRunNotice();
        return this.removeRenderedItem('thinking-placeholder');
    }

    private removeWelcome(): boolean {
        return this.removeRenderedItem('welcome');
    }

    private friendlyToolStatusLabel(messages: readonly ChatMessage[]): string {
        for (const message of messages) {
            for (const toolCall of message.toolCalls) {
                const label = this.options.toolLabels[toolCall.name] || '';
                if (label !== '') {
                    return label;
                }
            }
        }

        return '';
    }

    private upsertReasoningMessage(message: ChatMessage, isActive: boolean): boolean {
        if (!this.options.showReasoning) {
            return false;
        }

        let changed = this.removeWelcome();
        if (this.upsertRenderedItem({
            key: reasoningKey(message),
            kind: 'reasoning',
            message,
            isActive,
        })) {
            changed = true;
        }
        return changed;
    }

    private upsertVisibleMessage(message: ChatMessage, isStreaming: boolean): boolean {
        if (message.role === 'tool' || !this.shouldRenderMessage(message)) {
            return false;
        }

        let changed = this.removeWelcome();
        if (this.upsertRenderedItem({
            key: messageKey(message),
            kind: 'message',
            message,
            isStreaming,
        })) {
            changed = true;
        }
        return changed;
    }

    private shouldRenderMessage(message: ChatMessage): boolean {
        return message.content.trim() !== ''
            || (this.options.showToolCalls && message.toolCalls.length > 0);
    }

    private buildRenderItems(snapshot: ChatSnapshot): ChatRenderItem[] {
        const emptyMessage = this.options.openingQuestion || this.options.labels.emptyState;
        if (emptyMessage && snapshot.messages.length === 0) {
            return [{key: 'welcome', kind: 'welcome', message: emptyMessage}];
        }

        const lastUserIndex = findLastIndex(snapshot.messages, message => message.role === 'user');
        const activeAssistantHasText = snapshot.running && snapshot.messages.some((message, index) => (
            index > lastUserIndex
            && message.role === 'assistant'
            && message.content.trim() !== ''
        ));

        // Render every received AG-UI delta immediately. This id is only used for
        // a non-blocking CSS treatment on the message that is still streaming.
        const activeStreamingAssistantId = snapshot.running
            ? findLast(snapshot.messages, (message, index) => (
                index > lastUserIndex
                && message.role === 'assistant'
                && message.content.trim() !== ''
            ))?.id || null
            : null;
        // Only the current turn's latest reasoning item auto-opens. Older
        // completed reasoning summaries must stay collapsed during new runs.
        const activeReasoningId = snapshot.running && !activeAssistantHasText
            ? findLast(snapshot.messages, (message, index) => (
                index > lastUserIndex
                && message.role === 'reasoning'
            ))?.id || null
            : null;
        let hasPostUserWork = false;
        const items: ChatRenderItem[] = [];

        snapshot.messages.forEach((message, index) => {
            if (message.role === 'tool') {
                return;
            }

            if (index > lastUserIndex && isAssistantWork(message)) {
                hasPostUserWork = hasPostUserWork || !this.options.showFriendlyToolStatus
                    || (message.role === 'assistant' && message.content.trim() !== '');
            }

            if (message.role === 'reasoning') {
                if (this.options.showReasoning) {
                    items.push({
                        key: `reasoning:${message.id}`,
                        kind: 'reasoning',
                        message,
                        isActive: message.id === activeReasoningId,
                    });
                }
                return;
            }

            if (!this.shouldRenderMessage(message)) {
                return;
            }

            items.push({
                key: `message:${message.id}`,
                kind: 'message',
                message,
                isStreaming: message.id === activeStreamingAssistantId,
            });
        });

        if (snapshot.running && !hasPostUserWork) {
            const label = this.options.showFriendlyToolStatus
                ? this.friendlyToolStatusLabel(snapshot.messages.slice(lastUserIndex + 1))
                : '';
            items.push({
                key: 'thinking-placeholder',
                kind: 'thinking-placeholder',
                text: label || this.options.labels.thinking,
                status: label === '' ? 'thinking' : 'tool',
            });
        }

        return items;
    }

    private upsertRenderedItem(item: ChatRenderItem): boolean {
        const signature = renderItemSignature(item);
        const fingerprint = this.renderItemFingerprint(item);
        let rendered = this.renderedItems.get(item.key);
        const previousElement = rendered?.element || null;
        let changed = false;

        if (!rendered || rendered.signature !== signature) {
            if (rendered) {
                this.clearTextSmoothing(rendered);
            }
            rendered = this.createRenderedItem(item, signature, fingerprint);
            this.renderedItems.set(item.key, rendered);

            if (previousElement?.parentNode === this.messages) {
                previousElement.replaceWith(rendered.element);
            } else {
                this.messages.append(rendered.element);
                this.renderedOrder.push(item.key);
            }
            changed = true;
        } else if (rendered.fingerprint !== fingerprint) {
            if (this.updateRenderedItem(rendered, item)) {
                changed = true;
            }
            rendered.fingerprint = fingerprint;
        }

        if (rendered) {
            rendered.message = renderedItemMessage(item);
        }

        return changed;
    }

    private removeRenderedItem(key: string): boolean {
        const rendered = this.renderedItems.get(key);
        if (!rendered) {
            return false;
        }

        this.disposeRenderedItem(rendered);
        this.renderedItems.delete(key);
        const index = this.renderedOrder.indexOf(key);
        if (index !== -1) {
            this.renderedOrder.splice(index, 1);
        }
        return true;
    }

    private patchRenderedItems(items: ChatRenderItem[]): boolean {
        // Initial snapshots are append-heavy too; keep this path keyed so a
        // reset/replay does not depend on replaceChildren semantics.
        let changed = false;
        const nextOrder = items.map(item => item.key);
        const firstChangedIndex = firstDifferentIndex(this.renderedOrder, nextOrder);
        const liveKeys = new Set<string>();

        items.forEach((item, index) => {
            liveKeys.add(item.key);

            const signature = renderItemSignature(item);
            const fingerprint = this.renderItemFingerprint(item);
            let rendered = this.renderedItems.get(item.key);
            const previousElement = rendered?.element || null;
            let itemReplaced = false;

            if (!rendered || rendered.signature !== signature) {
                if (rendered) {
                    this.clearTextSmoothing(rendered);
                }
                rendered = this.createRenderedItem(item, signature, fingerprint);
                this.renderedItems.set(item.key, rendered);
                if (previousElement?.parentNode === this.messages) {
                    previousElement.replaceWith(rendered.element);
                }
                itemReplaced = true;
                changed = true;
            } else if (rendered.fingerprint !== fingerprint) {
                if (this.updateRenderedItem(rendered, item)) {
                    changed = true;
                }
                rendered.fingerprint = fingerprint;
            }

            if (itemReplaced || (firstChangedIndex !== -1 && index >= firstChangedIndex)) {
                const currentElement = this.messages.children[index] || null;
                if (currentElement !== rendered.element) {
                    this.messages.insertBefore(rendered.element, currentElement);
                    changed = true;
                }
            }

            rendered.message = renderedItemMessage(item);
        });

        this.renderedItems.forEach((rendered, key) => {
            if (!liveKeys.has(key)) {
                this.disposeRenderedItem(rendered);
                this.renderedItems.delete(key);
                changed = true;
            }
        });

        this.renderedOrder = nextOrder;
        return changed;
    }

    private createRenderedItem(item: ChatRenderItem, signature: string, fingerprint: string): RenderedItem {
        switch (item.kind) {
            case 'welcome':
                return {
                    kind: item.kind,
                    signature,
                    fingerprint,
                    element: this.renderWelcome(item.message),
                    content: item.message,
                    displayedContent: item.message,
                    smoothingFrame: null,
                    removeStreamingClassWhenSmooth: false,
                    toolCallsFingerprint: '',
                };

            case 'message': {
                const displayedContent = initialStreamingContent(item.message.content, item.isStreaming);
                const rendered: RenderedItem = {
                    kind: item.kind,
                    signature,
                    fingerprint,
                    element: this.renderMessage(item.message, item.isStreaming, displayedContent),
                    content: item.message.content,
                    displayedContent,
                    smoothingFrame: null,
                    removeStreamingClassWhenSmooth: false,
                    toolCallsFingerprint: toolCallsFingerprint(item.message.toolCalls, this.options.showToolCalls),
                    message: item.message,
                };
                if (displayedContent !== item.message.content) {
                    this.scheduleTextSmoothing(rendered);
                }
                return rendered;
            }

            case 'reasoning': {
                const reasoningContent = item.message.content.trim();
                const content = reasoningContent
                    || (item.isActive ? this.options.labels.thinking + '...' : this.formatThoughtLabel(item.message, false));
                const displayedContent = reasoningContent === ''
                    ? content
                    : initialStreamingContent(content, item.isActive);
                const rendered: RenderedItem = {
                    kind: item.kind,
                    signature,
                    fingerprint,
                    element: this.renderThinking(item.message, item.isActive, displayedContent),
                    content,
                    displayedContent,
                    smoothingFrame: null,
                    removeStreamingClassWhenSmooth: false,
                    toolCallsFingerprint: '',
                    message: item.message,
                };
                if (displayedContent !== content) {
                    this.scheduleTextSmoothing(rendered);
                }
                return rendered;
            }

            case 'thinking-placeholder':
                return {
                    kind: item.kind,
                    signature,
                    fingerprint,
                    element: this.renderThinkingPlaceholder(item.text, item.status),
                    content: item.text,
                    displayedContent: item.text,
                    smoothingFrame: null,
                    removeStreamingClassWhenSmooth: false,
                    toolCallsFingerprint: '',
                };
        }
    }

    private updateRenderedItem(rendered: RenderedItem, item: ChatRenderItem): boolean {
        switch (item.kind) {
            case 'welcome':
                return this.updateWelcome(rendered, item.message);

            case 'message':
                return this.updateMessage(rendered, item.message, item.isStreaming);

            case 'reasoning':
                return this.updateThinking(rendered, item.message, item.isActive);

            case 'thinking-placeholder':
                return this.updateThinkingPlaceholder(rendered, item);
        }
    }

    private renderItemFingerprint(item: ChatRenderItem): string {
        switch (item.kind) {
            case 'welcome':
                return `${item.kind}\u0000${item.message}`;

            case 'thinking-placeholder':
                return `${item.kind}\u0000${item.text}\u0000${item.status}`;

            case 'reasoning': {
                const content = item.message.content.trim();
                const label = content === ''
                    ? (item.isActive ? this.options.labels.thinking + '...' : this.formatThoughtLabel(item.message, false))
                    : this.formatThoughtLabel(item.message, item.isActive);
                return [
                    item.kind,
                    item.isActive ? 'active' : '',
                    content,
                    label,
                ].join('\u0000');
            }

            case 'message':
                return [
                    item.kind,
                    item.message.role,
                    item.isStreaming ? 'streaming' : '',
                    item.message.content,
                    toolCallsFingerprint(item.message.toolCalls, this.options.showToolCalls),
                ].join('\u0000');
        }
    }

    private renderStatus(snapshot: Pick<ChatSnapshot, 'error' | 'errorCode'>): void {
        const errorText = snapshot.error
            ? (snapshot.errorCode === 'agent_run_timeout'
                ? this.options.labels.runTimeoutWarning || snapshot.error
                : snapshot.error)
            : '';
        const signature = `${snapshot.errorCode || ''}\u0000${errorText}`;
        if (this.statusSignature === signature) {
            return;
        }

        this.statusSignature = signature;
        this.status.replaceChildren();
        if (!errorText) {
            return;
        }

        const icon = document.createElement('i');
        icon.className = 'fa-light fa-triangle-exclamation howspace-ai-chat-status-icon';
        icon.setAttribute('aria-hidden', 'true');

        const text = document.createElement('span');
        text.textContent = errorText;

        this.status.append(icon, text);
    }

    private renderWelcome(message: string): HTMLElement {
        const welcome = document.createElement('section');
        welcome.className = 'howspace-ai-chat-welcome';

        const title = document.createElement('h2');
        title.textContent = message;
        welcome.append(title);

        return welcome;
    }

    private scrollToBottom(): void {
        const scroll = (): void => {
            this.messages.scrollTop = this.messages.scrollHeight;
        };

        scroll();
        window.requestAnimationFrame(scroll);
    }

    private isNearBottom(): boolean {
        const remaining = this.messages.scrollHeight - this.messages.clientHeight - this.messages.scrollTop;
        return remaining <= STICKY_SCROLL_THRESHOLD_PX;
    }

    private disposeRenderedItem(rendered: RenderedItem): void {
        // Replacements/removals must cancel the pending smoothing frame;
        // otherwise a stale active item could write back into detached DOM.
        this.clearTextSmoothing(rendered);
        rendered.element.remove();
    }

    private renderMessage(message: ChatMessage, isStreaming = false, content = message.content): HTMLElement {
        const item = document.createElement('article');
        const body = document.createElement('div');
        body.className = 'howspace-ai-chat-message-body';
        item.append(body);

        item.addEventListener('mouseleave', () => {
            const copyButton = item.querySelector<HTMLButtonElement>('.howspace-ai-chat-copy');
            copyButton?.dispatchEvent(new Event('coach-chat-copy-reset'));
        });

        this.updateMessageElement(item, message, isStreaming);
        this.updateMessageContent(item, content, true, message.content);
        this.updateToolCalls(item, message.toolCalls);

        return item;
    }

    private updateWelcome(rendered: RenderedItem, message: string): boolean {
        if (rendered.content === message) {
            return false;
        }

        const title = rendered.element.querySelector('h2');
        if (title) {
            title.textContent = message;
        }
        rendered.content = message;
        return true;
    }

    private updateMessage(rendered: RenderedItem, message: ChatMessage, isStreaming: boolean): boolean {
        let changed = false;
        const previousContent = rendered.content;
        rendered.message = message;

        const shouldSmoothText = this.shouldSmoothMessageText(rendered, message, isStreaming, previousContent);
        rendered.content = message.content;
        if (this.updateMessageElement(rendered.element, message, isStreaming || shouldSmoothText)) {
            changed = true;
        }

        if (shouldSmoothText) {
            if (this.updateSmoothedMessageContent(rendered, message.content, !isStreaming)) {
                changed = true;
            }
        } else if (this.updateImmediateMessageContent(rendered, message.content)) {
            changed = true;
        }

        const nextToolCallsFingerprint = toolCallsFingerprint(message.toolCalls, this.options.showToolCalls);
        if (rendered.toolCallsFingerprint !== nextToolCallsFingerprint) {
            if (this.updateToolCalls(rendered.element, message.toolCalls)) {
                changed = true;
            }
            rendered.toolCallsFingerprint = nextToolCallsFingerprint;
        }

        return changed;
    }

    private shouldSmoothMessageText(
        rendered: RenderedItem,
        message: ChatMessage,
        isStreaming: boolean,
        previousContent: string,
    ): boolean {
        return message.role === 'assistant'
            && message.content.trim() !== ''
            && (isStreaming || rendered.displayedContent !== message.content)
            && (previousContent !== message.content || rendered.displayedContent !== message.content);
    }

    private updateSmoothedMessageContent(
        rendered: RenderedItem,
        targetContent: string,
        removeStreamingClassWhenSmooth: boolean,
    ): boolean {
        // Keep copy using the canonical content even while the DOM shows a paced prefix.
        rendered.removeStreamingClassWhenSmooth = removeStreamingClassWhenSmooth;
        let changed = this.updateMessageContent(rendered.element, rendered.displayedContent, false, targetContent);

        if (!targetContent.startsWith(rendered.displayedContent)) {
            this.clearTextSmoothing(rendered);
            rendered.removeStreamingClassWhenSmooth = false;
            changed = this.updateImmediateMessageContent(rendered, targetContent) || changed;
            if (removeStreamingClassWhenSmooth && rendered.message) {
                changed = this.updateMessageElement(rendered.element, rendered.message, false) || changed;
            }
            return changed;
        }

        if (rendered.displayedContent === targetContent) {
            this.clearTextSmoothing(rendered);
            if (removeStreamingClassWhenSmooth && rendered.message) {
                return this.updateMessageElement(rendered.element, rendered.message, false);
            }
            return false;
        }

        this.scheduleTextSmoothing(rendered);
        return true;
    }

    private updateImmediateMessageContent(rendered: RenderedItem, content: string): boolean {
        this.clearTextSmoothing(rendered);
        const contentChanged = rendered.displayedContent !== content;
        rendered.displayedContent = content;
        rendered.removeStreamingClassWhenSmooth = false;
        return this.updateMessageContent(rendered.element, content, contentChanged, content);
    }

    private updateSmoothedThinkingContent(
        rendered: RenderedItem,
        targetContent: string,
        removeStreamingClassWhenSmooth: boolean,
    ): boolean {
        rendered.removeStreamingClassWhenSmooth = removeStreamingClassWhenSmooth;

        if (!targetContent.startsWith(rendered.displayedContent)) {
            this.clearTextSmoothing(rendered);
            rendered.removeStreamingClassWhenSmooth = false;
            let changed = this.updateImmediateThinkingContent(rendered, targetContent);
            if (removeStreamingClassWhenSmooth) {
                changed = this.updateThinkingElement(rendered.element, false) || changed;
            }
            return changed;
        }

        if (rendered.displayedContent === targetContent) {
            this.clearTextSmoothing(rendered);
            if (removeStreamingClassWhenSmooth) {
                return this.updateThinkingElement(rendered.element, false);
            }
            return false;
        }

        this.scheduleTextSmoothing(rendered);
        return true;
    }

    private updateImmediateThinkingContent(rendered: RenderedItem, content: string): boolean {
        this.clearTextSmoothing(rendered);
        const contentChanged = rendered.displayedContent !== content;
        rendered.displayedContent = content;
        rendered.removeStreamingClassWhenSmooth = false;
        return this.updateThinkingContent(rendered.element, content, contentChanged);
    }

    private scheduleTextSmoothing(rendered: RenderedItem): void {
        if (rendered.smoothingFrame !== null) {
            return;
        }

        // Performance: this is not a global/permanent animation loop. At most one
        // rAF is pending per active rendered item, and each frame patches only that
        // item's text node from the latest canonical content.
        rendered.smoothingFrame = window.requestAnimationFrame(() => {
            rendered.smoothingFrame = null;
            this.advanceTextSmoothing(rendered);
        });
    }

    private advanceTextSmoothing(rendered: RenderedItem): void {
        if (!rendered.element.isConnected) {
            this.clearTextSmoothing(rendered);
            return;
        }

        const stickToBottom = this.isNearBottom();
        const targetContent = rendered.content;
        const nextContent = nextStreamingContent(rendered.displayedContent, targetContent);
        const previousContent = rendered.displayedContent;
        rendered.displayedContent = nextContent;
        this.updateSmoothedContentFrame(rendered, nextContent, previousContent !== nextContent, targetContent);

        if (nextContent !== targetContent) {
            if (stickToBottom) {
                this.scrollToBottom();
            }
            this.scheduleTextSmoothing(rendered);
            return;
        }

        if (rendered.removeStreamingClassWhenSmooth) {
            this.finishTextSmoothing(rendered);
            rendered.removeStreamingClassWhenSmooth = false;
        }
        if (stickToBottom) {
            this.scrollToBottom();
        }
    }

    private clearTextSmoothing(rendered: RenderedItem): void {
        if (rendered.smoothingFrame !== null) {
            window.cancelAnimationFrame(rendered.smoothingFrame);
            rendered.smoothingFrame = null;
        }
    }

    private updateSmoothedContentFrame(
        rendered: RenderedItem,
        content: string,
        contentChanged: boolean,
        targetContent: string,
    ): boolean {
        if (rendered.kind === 'reasoning') {
            return this.updateThinkingContent(rendered.element, content, contentChanged);
        }

        return this.updateMessageContent(rendered.element, content, contentChanged, targetContent);
    }

    private finishTextSmoothing(rendered: RenderedItem): boolean {
        if (rendered.kind === 'reasoning') {
            return this.updateThinkingElement(rendered.element, false);
        }

        return rendered.message ? this.updateMessageElement(rendered.element, rendered.message, false) : false;
    }

    private updateMessageElement(element: HTMLElement, message: ChatMessage, isStreaming: boolean): boolean {
        const className = `howspace-ai-chat-message is-${message.role}${isStreaming ? ' is-streaming' : ''}`;
        if (element.className === className) {
            return false;
        }

        element.className = className;
        return true;
    }

    private updateMessageContent(
        element: HTMLElement,
        content: string,
        contentChanged: boolean,
        copyContent = content,
    ): boolean {
        const body = element.querySelector<HTMLElement>('.howspace-ai-chat-message-body');
        if (!body) {
            return false;
        }

        let changed = false;
        let contentElement = body.querySelector<HTMLElement>('.howspace-ai-chat-message-content');
        let copyButton = element.querySelector<HTMLButtonElement>('.howspace-ai-chat-copy');

        if (content.trim() === '') {
            if (contentElement) {
                contentElement.remove();
                changed = true;
            }
            if (copyButton) {
                copyButton.remove();
                changed = true;
            }
            return changed;
        }

        if (!contentElement) {
            contentElement = document.createElement('div');
            contentElement.className = 'howspace-ai-chat-message-content';
            body.insertBefore(contentElement, body.querySelector('.howspace-ai-chat-tools'));
            contentChanged = true;
            changed = true;
        }

        if (contentChanged) {
            contentElement.innerHTML = renderMarkdown(content);
            changed = true;
        }

        if (!copyButton) {
            copyButton = this.renderCopyButton(copyContent);
            element.append(copyButton);
            changed = true;
        } else {
            this.copyButtonContent.set(copyButton, copyContent);
        }

        return changed;
    }

    private updateThinking(rendered: RenderedItem, message: ChatMessage, isActive: boolean): boolean {
        const content = message.content.trim();
        let changed = false;
        const previousContent = rendered.content;
        rendered.message = message;

        if (content === '') {
            this.clearTextSmoothing(rendered);
            const text = isActive ? this.options.labels.thinking + '...' : this.formatThoughtLabel(message, false);
            if (rendered.content !== text) {
                if (!isActive) {
                    rendered.element.textContent = text;
                }
                rendered.content = text;
                rendered.displayedContent = text;
                changed = true;
            }
            return changed;
        }

        const shouldSmoothText = this.shouldSmoothThinkingText(rendered, isActive, previousContent, content);
        rendered.content = content;
        if (this.updateThinkingElement(rendered.element, isActive || shouldSmoothText)) {
            changed = true;
        }

        const summary = rendered.element.querySelector('summary');
        const label = this.formatThoughtLabel(message, isActive);
        if (summary && updateLeadingTextNode(summary, label)) {
            changed = true;
        }

        if (shouldSmoothText) {
            if (this.updateSmoothedThinkingContent(rendered, content, !isActive)) {
                changed = true;
            }
        } else if (this.updateImmediateThinkingContent(rendered, content)) {
            changed = true;
        }

        return changed;
    }

    private shouldSmoothThinkingText(
        rendered: RenderedItem,
        isActive: boolean,
        previousContent: string,
        content: string,
    ): boolean {
        return content !== ''
            && (isActive || rendered.displayedContent !== content)
            && (previousContent !== content || rendered.displayedContent !== content);
    }

    private updateThinkingElement(element: HTMLElement, isStreaming: boolean): boolean {
        if (!(element instanceof HTMLDetailsElement)) {
            return false;
        }

        let changed = false;
        const className = `howspace-ai-chat-thinking${isStreaming ? ' is-streaming' : ''}`;
        if (element.className !== className) {
            element.className = className;
            changed = true;
        }

        if (element.open !== isStreaming) {
            element.open = isStreaming;
            changed = true;
        }

        return changed;
    }

    private updateThinkingContent(element: HTMLElement, content: string, contentChanged: boolean): boolean {
        const body = element.querySelector<HTMLElement>('.howspace-ai-chat-thinking-content');
        if (!body) {
            return false;
        }

        if (contentChanged) {
            body.innerHTML = renderMarkdown(content);
            return true;
        }

        return false;
    }

    private renderThinking(message: ChatMessage, runIsActive: boolean, content = message.content.trim()): HTMLElement {
        if (message.content.trim() === '') {
            return runIsActive
                ? this.renderThinkingPlaceholder(this.options.labels.thinking, 'thinking')
                : this.renderThinkingSummary(message);
        }

        const details = document.createElement('details');
        details.className = `howspace-ai-chat-thinking${runIsActive ? ' is-streaming' : ''}`;
        details.open = runIsActive;

        const summary = document.createElement('summary');
        summary.append(document.createTextNode(this.formatThoughtLabel(message, runIsActive)), this.renderChevronIcon());
        details.append(summary);

        const body = document.createElement('div');
        body.className = 'howspace-ai-chat-thinking-content';
        body.innerHTML = renderMarkdown(content);
        details.append(body);

        return details;
    }

    private renderThinkingPlaceholder(textContent: string, status: TransientRunStatus): HTMLElement {
        const placeholder = document.createElement('div');
        placeholder.className = 'howspace-ai-chat-thinking is-pending';
        placeholder.setAttribute('role', 'status');

        const text = document.createElement('span');
        text.className = 'howspace-ai-chat-thinking-text';
        text.textContent = textContent;

        const dots = this.renderThinkingDots();
        dots.hidden = status !== 'thinking';

        placeholder.append(
            text,
            dots,
        );
        return placeholder;
    }

    private updateThinkingPlaceholder(
        rendered: RenderedItem,
        item: Extract<ChatRenderItem, {kind: 'thinking-placeholder'}>,
    ): boolean {
        let changed = false;
        const text = rendered.element.querySelector<HTMLElement>('.howspace-ai-chat-thinking-text');
        if (text && text.textContent !== item.text) {
            text.textContent = item.text;
            changed = true;
        }

        if (this.setThinkingPlaceholderStatus(item.status)) {
            changed = true;
        }

        rendered.content = item.text;
        rendered.displayedContent = item.text;
        return changed;
    }

    private transitionThinkingPlaceholder(target: string): void {
        this.clearQueuedRunNoticeTimer();
        if (this.thinkingPlaceholderFrame !== null) {
            window.cancelAnimationFrame(this.thinkingPlaceholderFrame);
            this.thinkingPlaceholderFrame = null;
        }

        const text = this.thinkingPlaceholderTextElement();
        if (!text || text.textContent === target) {
            return;
        }

        this.setThinkingPlaceholderStatus('tool');
        this.animateThinkingPlaceholder(text.textContent || '', target, 'erase');
    }

    // The erasing support was added during websocket integration so that when the worker queue is under congestion,
    // we can smoothly replace the standard "Thinking..." placeholder with a notification about it.
    private animateThinkingPlaceholder(current: string, target: string, phase: 'erase' | 'type'): void {
        this.thinkingPlaceholderFrame = window.requestAnimationFrame(() => {
            const text = this.thinkingPlaceholderTextElement();
            if (!text) {
                this.thinkingPlaceholderFrame = null;
                return;
            }

            if (phase === 'erase' && current !== '') {
                const next = current.slice(0, Math.max(0, current.length - THINKING_PLACEHOLDER_ERASE_CHARS));
                text.textContent = next;
                this.animateThinkingPlaceholder(next, target, 'erase');
                return;
            }

            if (phase === 'erase') {
                this.animateThinkingPlaceholder('', target, 'type');
                return;
            }

            const nextLength = Math.min(target.length, current.length + THINKING_PLACEHOLDER_TYPE_CHARS);
            const next = target.slice(0, nextLength);
            text.textContent = next;
            if (next !== target) {
                this.animateThinkingPlaceholder(next, target, 'type');
                return;
            }

            this.thinkingPlaceholderFrame = null;
        });
    }

    private resetThinkingPlaceholder(): void {
        const text = this.thinkingPlaceholderTextElement();
        if (!text) {
            return;
        }

        text.textContent = this.options.labels.thinking;
        this.setThinkingPlaceholderStatus('thinking');
    }

    private thinkingPlaceholderTextElement(): HTMLElement | null {
        return this.renderedItems
            .get('thinking-placeholder')
            ?.element
            .querySelector<HTMLElement>('.howspace-ai-chat-thinking-text') || null;
    }

    private setThinkingPlaceholderStatus(status: TransientRunStatus): boolean {
        const dots = this.renderedItems
            .get('thinking-placeholder')
            ?.element
            .querySelector<HTMLElement>('.howspace-ai-chat-thinking-dots');
        if (dots) {
            const visible = status === 'thinking';
            if (dots.hidden === !visible) {
                return false;
            }
            dots.hidden = !visible;
            return true;
        }

        return false;
    }

    private renderThinkingSummary(message: ChatMessage): HTMLElement {
        const summary = document.createElement('div');
        summary.className = 'howspace-ai-chat-thinking is-empty';
        summary.textContent = this.formatThoughtLabel(message, false);
        return summary;
    }

    private renderThinkingDots(): HTMLElement {
        const dots = document.createElement('span');
        dots.className = 'howspace-ai-chat-thinking-dots';
        dots.setAttribute('aria-hidden', 'true');
        dots.textContent = '...';
        return dots;
    }

    private renderChevronIcon(): HTMLElement {
        const icon = document.createElement('i');
        icon.className = 'fa-solid fa-chevron-right howspace-ai-chat-chevron';
        icon.setAttribute('aria-hidden', 'true');
        return icon;
    }

    private updateToolCalls(element: HTMLElement, toolCalls: ChatToolCall[]): boolean {
        const body = element.querySelector<HTMLElement>('.howspace-ai-chat-message-body');
        let tools = body?.querySelector<HTMLElement>('.howspace-ai-chat-tools') || null;
        if (!body) {
            return false;
        }

        if (!this.options.showToolCalls || toolCalls.length === 0) {
            if (tools) {
                tools.remove();
                return true;
            }
            return false;
        }

        let changed = false;
        if (!tools) {
            tools = document.createElement('div');
            tools.className = 'howspace-ai-chat-tools';
            body.append(tools);
            changed = true;
        }

        const liveToolCallIds = new Set<string>();
        toolCalls.forEach((toolCall, index) => {
            let toolElement = findToolCallElement(tools, toolCall.id);
            if (toolElement) {
                if (this.updateToolCall(toolElement, toolCall)) {
                    changed = true;
                }
            } else {
                toolElement = this.renderToolCall(toolCall);
                changed = true;
            }
            liveToolCallIds.add(toolCall.id);

            const currentElement = tools.children[index] || null;
            if (currentElement !== toolElement) {
                tools.insertBefore(toolElement, currentElement);
                changed = true;
            }
        });

        Array.from(tools.children).forEach(toolElement => {
            if (
                toolElement instanceof HTMLElement
                && !liveToolCallIds.has(toolElement.dataset.toolCallId || '')
            ) {
                toolElement.remove();
                changed = true;
            }
        });

        return changed;
    }

    private renderToolCall(toolCall: ChatToolCall): HTMLElement {
        const details = document.createElement('details');
        details.className = `howspace-ai-chat-tool is-${toolCall.status}`;
        details.dataset.toolCallId = toolCall.id;

        const summary = document.createElement('summary');
        summary.append(
            this.renderChevronIcon(),
            this.renderToolStatusDot(),
            this.renderToolName(toolCall.name),
            this.renderToolStatus(toolCall.status),
        );

        const body = document.createElement('div');
        body.className = 'howspace-ai-chat-tool-body';
        body.append(
            this.renderToolPayload(this.options.labels.toolArguments, toolCall.args, 'args'),
            this.renderToolPayload(this.options.labels.toolResult, toolCall.result || '', 'result'),
        );

        details.append(summary, body);
        return details;
    }

    private updateToolCall(toolElement: HTMLElement, toolCall: ChatToolCall): boolean {
        let changed = false;
        const className = `howspace-ai-chat-tool is-${toolCall.status}`;
        if (toolElement.className !== className) {
            toolElement.className = className;
            changed = true;
        }

        const name = toolElement.querySelector<HTMLElement>('.howspace-ai-chat-tool-name');
        if (name && name.textContent !== toolCall.name) {
            name.textContent = toolCall.name;
            changed = true;
        }

        const status = toolElement.querySelector<HTMLElement>('.howspace-ai-chat-tool-status');
        const statusText = this.toolStatusLabel(toolCall.status);
        if (status && status.textContent !== statusText) {
            status.textContent = statusText;
            changed = true;
        }

        if (this.updateToolPayload(toolElement, 'args', toolCall.args)) {
            changed = true;
        }
        if (this.updateToolPayload(toolElement, 'result', toolCall.result || '')) {
            changed = true;
        }

        return changed;
    }

    private renderToolStatusDot(): HTMLElement {
        const dot = document.createElement('span');
        dot.className = 'howspace-ai-chat-tool-dot';
        dot.setAttribute('aria-hidden', 'true');
        return dot;
    }

    private renderToolName(name: string): HTMLElement {
        const toolName = document.createElement('span');
        toolName.className = 'howspace-ai-chat-tool-name';
        toolName.textContent = name;
        return toolName;
    }

    private renderToolStatus(status: ChatToolCallStatus): HTMLElement {
        const badge = document.createElement('span');
        badge.className = 'howspace-ai-chat-tool-status';
        badge.textContent = this.toolStatusLabel(status);
        return badge;
    }

    private renderToolPayload(label: string, raw: string, kind: 'args' | 'result'): HTMLElement {
        const section = document.createElement('section');
        section.className = 'howspace-ai-chat-tool-payload';
        section.dataset.payload = kind;

        const heading = document.createElement('div');
        heading.className = 'howspace-ai-chat-tool-payload-label';
        heading.textContent = label;

        const pre = document.createElement('pre');
        pre.textContent = raw ? formatJson(tryParseJson(raw)) : '';

        section.append(heading, pre);
        return section;
    }

    private updateToolPayload(toolElement: HTMLElement, kind: 'args' | 'result', raw: string): boolean {
        const pre = Array.from(toolElement.querySelectorAll<HTMLElement>('.howspace-ai-chat-tool-payload'))
            .find(section => section.dataset.payload === kind)
            ?.querySelector('pre');
        if (!pre) {
            return false;
        }

        const nextText = raw ? formatJson(tryParseJson(raw)) : '';
        if (pre.textContent !== nextText) {
            pre.textContent = nextText;
            return true;
        }

        return false;
    }

    private toolStatusLabel(status: ChatToolCallStatus): string {
        return {
            running: this.options.labels.toolStatusRunning,
            done: this.options.labels.toolStatusDone,
            error: this.options.labels.toolStatusError,
        }[status];
    }

    private renderCopyButton(content: string): HTMLButtonElement {
        const button = document.createElement('button');
        let hideFeedbackTimer: number | undefined;
        const renderCopyIcon = (): void => {
            button.innerHTML = '<i class="fa-light fa-copy" aria-hidden="true"></i>';
        };
        const resetFeedback = (): void => {
            if (hideFeedbackTimer !== undefined) {
                window.clearTimeout(hideFeedbackTimer);
                hideFeedbackTimer = undefined;
            }
            button.classList.remove('is-copied', 'is-copy-hidden');
            renderCopyIcon();
        };

        button.type = 'button';
        button.className = 'howspace-ai-chat-copy';
        button.setAttribute('aria-label', this.options.labels.copy);
        button.title = this.options.labels.copy;
        this.copyButtonContent.set(button, content);
        renderCopyIcon();
        button.addEventListener('click', async () => {
            await copyText(this.copyButtonContent.get(button) || '');
            if (hideFeedbackTimer !== undefined) {
                window.clearTimeout(hideFeedbackTimer);
            }
            button.classList.remove('is-copy-hidden');
            button.classList.add('is-copied');
            button.innerHTML = '<i class="fa-light fa-circle-check" aria-hidden="true"></i>';
            hideFeedbackTimer = window.setTimeout(() => {
                button.classList.remove('is-copied');
                renderCopyIcon();
                button.classList.add('is-copy-hidden');
                hideFeedbackTimer = undefined;
            }, 550);
        });
        button.addEventListener('blur', resetFeedback);
        button.addEventListener('focus', () => {
            button.classList.remove('is-copy-hidden');
        });
        button.addEventListener('coach-chat-copy-reset', resetFeedback);
        return button;
    }

    private formatThoughtLabel(message: ChatMessage, runIsActive: boolean): string {
        if (runIsActive) {
            return this.options.labels.thinking;
        }

        const durationMs = message.durationMs ?? Math.max(0, message.updatedAt - message.createdAt);
        const seconds = Math.round(durationMs / 1000);
        if (seconds <= 1) {
            return `${this.options.labels.thoughtFor} ${this.options.labels.fewSeconds}`;
        }

        return `${this.options.labels.thoughtFor} ${seconds} ${this.options.labels.seconds}`;
    }

    private resizeTextarea(): void {
        this.textarea.style.height = 'auto';
        this.textarea.style.height = `${this.textarea.scrollHeight}px`;
    }

    private updateSubmitState(): void {
        const unavailable = isActivityIdle();
        if (this.textarea.disabled !== unavailable) {
            this.textarea.disabled = unavailable;
        }

        const disabled = unavailable || this.running || this.textarea.value.trim() === '';
        if (this.submitButton.disabled !== disabled) {
            this.submitButton.disabled = disabled;
        }
    }
}

const isActivityIdle = (): boolean => (
    typeof howspace !== 'undefined'
        && typeof howspace.activityTracker?.isIdle === 'function'
        && howspace.activityTracker.isIdle()
);

function findLastIndex<T>(items: readonly T[], predicate: (item: T) => boolean): number {
    for (let index = items.length - 1; index >= 0; index -= 1) {
        if (predicate(items[index])) {
            return index;
        }
    }

    return -1;
}

function findLast<T>(items: readonly T[], predicate: (item: T, index: number) => boolean): T | null {
    for (let index = items.length - 1; index >= 0; index -= 1) {
        if (predicate(items[index], index)) {
            return items[index];
        }
    }

    return null;
}

function initialStreamingContent(content: string, isStreaming: boolean): string {
    if (!isStreaming || content.length <= STREAM_INITIAL_VISIBLE_CHARS) {
        return content;
    }

    const firstVisibleCharacter = content.search(/\S/);
    const initialLength = Math.max(STREAM_INITIAL_VISIBLE_CHARS, firstVisibleCharacter + 1);
    return content.slice(0, initialLength);
}

function nextStreamingContent(current: string, target: string): string {
    if (!target.startsWith(current)) {
        return target;
    }

    const remaining = target.length - current.length;
    if (remaining <= 0) {
        return target;
    }

    const chunkSize = Math.min(STREAM_SMOOTHING_MAX_CHARS, Math.max(1, Math.ceil(remaining / 10)));
    return target.slice(0, current.length + chunkSize);
}

function isAssistantWork(message: ChatMessage): boolean {
    return message.role === 'reasoning' || (
        message.role === 'assistant' && (message.content.trim() !== '' || message.toolCalls.length > 0)
    );
}

function messageKey(message: ChatMessage): string {
    return `message:${message.id}`;
}

function reasoningKey(message: ChatMessage): string {
    return `reasoning:${message.id}`;
}

function firstDifferentIndex<T>(left: readonly T[], right: readonly T[]): number {
    const length = Math.max(left.length, right.length);
    for (let index = 0; index < length; index += 1) {
        if (left[index] !== right[index]) {
            return index;
        }
    }

    return -1;
}

function renderItemSignature(item: ChatRenderItem): string {
    switch (item.kind) {
        case 'welcome':
        case 'thinking-placeholder':
            return item.kind;

        case 'message':
            return item.kind;

        case 'reasoning': {
            const content = item.message.content.trim();
            // Keep details stable while active reasoning finishes so the
            // visible buffer can catch up before the block collapses.
            return [
                item.kind,
                content === '' ? (item.isActive ? 'pending' : 'summary') : 'details',
            ].join('\u0000');
        }
    }
}

function renderedItemMessage(item: ChatRenderItem): ChatMessage | undefined {
    return item.kind === 'message' || item.kind === 'reasoning' ? item.message : undefined;
}

function findToolCallElement(container: HTMLElement, toolCallId: string): HTMLElement | null {
    return Array.from(container.children)
        .find((item): item is HTMLElement => (
            item instanceof HTMLElement && item.dataset.toolCallId === toolCallId
        )) || null;
}

function toolCallsFingerprint(toolCalls: readonly ChatToolCall[], showToolCalls: boolean): string {
    if (!showToolCalls) {
        return '';
    }

    return toolCalls.map(toolCall => [
        toolCall.id,
        toolCall.name,
        toolCall.status,
        toolCall.args,
        toolCall.result || '',
    ].join('\u0001')).join('\u0002');
}

function updateLeadingTextNode(element: HTMLElement, text: string): boolean {
    const firstNode = element.firstChild;
    if (firstNode?.nodeType === Node.TEXT_NODE) {
        if (firstNode.textContent === text) {
            return false;
        }
        firstNode.textContent = text;
        return true;
    }

    element.prepend(document.createTextNode(text));
    return true;
}

function renderMarkdown(input: string): string {
    const html = marked.parse(input || '', {
        async: false,
        breaks: false,
        gfm: true,
    }) as string;

    return DOMPurify.sanitize(html, {
        ALLOWED_ATTR: ['href', 'title'],
        ALLOWED_TAGS: [
            'a',
            'blockquote',
            'br',
            'code',
            'del',
            'em',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'hr',
            'li',
            'ol',
            'p',
            'pre',
            'strong',
            'table',
            'tbody',
            'td',
            'th',
            'thead',
            'tr',
            'ul',
        ],
    });
}

function tryParseJson(input: string): unknown {
    if (!input) {
        return null;
    }

    try {
        return JSON.parse(input);
    } catch {
        return input;
    }
}

function formatJson(value: unknown): string {
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

async function copyText(content: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
        return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.append(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
}
